import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import i18n from '@/i18n'
import { resolveLanguage } from '@/i18n/locale-resolution'
import { selectIsAuth, selectUserData } from '@/reducers/userDataReducer'
import { isImpersonating } from '@/utils/auth'

import { readPreferredLanguage, writePreferredLanguage } from './preferred-language'
import { selectLocaleBuilding } from './select-locale-building'

export type UserLocaleSyncProps = {
  children: React.ReactNode
}

/**
 * Keeps the active language in line with the backend (EL-13870), mobile's LocaleSync rules:
 * translations off for the building (`i18nAvailable`) → en-US; otherwise the account's
 * `user.locale` if the building allows it (`availableLocale`), else the user's last language in
 * this browser (preferred-language.ts), else the building's default (`locale`). See
 * resolveLanguage in src/i18n/locale-resolution.ts and selectLocaleBuilding for which building
 * counts.
 *
 * Re-applies on login, when the building loads or changes, and after the Profile → Language picker
 * (UserProfile.jsx) saves user.locale. Until the building is loaded it keeps the language on screen
 * (cached in localStorage), so a reload doesn't flash English. Logged out, it leaves the language
 * alone. Skipped while impersonating: the support user's own browser must not switch to - and
 * cache - the impersonated user's language.
 */
export const UserLocaleSync = ({ children }: UserLocaleSyncProps) => {
  // TODO: migrate off redux
  const isAuth = useSelector(selectIsAuth)
  const userData = useSelector(selectUserData)
  const building = useSelector(selectLocaleBuilding)
  const userId: number | string | undefined = userData?.user?.id
  const userLocale: string | null | undefined = userData?.user?.locale

  useEffect(() => {
    if (!isAuth || isImpersonating()) return

    const preferredLocale = readPreferredLanguage(userId)
    const language = resolveLanguage({
      building,
      current: i18n.language,
      preferredLocale,
      userLocale,
    })
    if (i18n.language !== language) i18n.changeLanguage(language)

    // Seeds the remembered language with the first one the user gets on a translated building, so
    // moving on to another translated building keeps it. Never overwritten from here - only by a
    // pick in the Profile picker - so a building that doesn't offer it can't erase it. Not seeded
    // from a building with translations off: its forced en-US isn't the user's language.
    if (building?.i18nAvailable === true && !preferredLocale)
      writePreferredLanguage(userId, language)
  }, [isAuth, building, userId, userLocale])

  return <>{children}</>
}
